import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, fmtEuro, CLAIM_REASONS, CLAIM_STATUS } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminLayout";
import { ClaimStatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const History = ({ c }) => (
  <div className="space-y-4">
    <div><p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Cronologia pubblica</p>
      {c.timeline.map((t, i) => <div key={i} className="text-sm border-l-2 border-emerald-900 pl-3 mb-2"><ClaimStatusBadge status={t.status} /> <span className="text-xs text-gray-500">{fmtDateTime(t.at)}</span>{t.message && <p className="mt-1">{t.message}</p>}</div>)}
    </div>
    {c.notes.length > 0 && <div><p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Note interne</p>
      {c.notes.map((n, i) => <p key={i} className="text-sm bg-amber-50 border border-amber-100 rounded-md p-2 mb-2">{n.text} <span className="block text-xs text-gray-500">{fmtDateTime(n.at)}</span></p>)}
    </div>}
  </div>
);

const ClaimSheet = ({ claim, onClose, onSaved }) => {
  const [f, setF] = useState({ status: "", public_message: "", note: "", refund_amount: "" });
  useEffect(() => { if (claim) setF({ status: claim.status, public_message: "", note: "", refund_amount: claim.refund_amount ?? "" }); }, [claim]);
  const save = async () => {
    try {
      const body = { ...f, refund_amount: f.refund_amount === "" ? null : Number(f.refund_amount) };
      const { data } = await api.patch(`/admin/claims/${claim.id}`, body);
      toast.success("Pratica aggiornata"); onSaved(data);
      setF({ ...f, public_message: "", note: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Sheet open={!!claim} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="claim-sheet">
        {claim && <>
          <SheetHeader><SheetTitle className="font-mono">{claim.claim_number}</SheetTitle></SheetHeader>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-gray-500 text-xs">Cliente</p><p className="font-medium">{claim.name}</p><p className="text-xs">{claim.email}</p></div>
            <div><p className="text-gray-500 text-xs">Ordine</p><p className="font-medium">#{claim.order_number}</p><p className="text-xs">Totale {fmtEuro(claim.order_total)}</p></div>
            <div><p className="text-gray-500 text-xs">Motivo</p><p className="font-medium">{CLAIM_REASONS[claim.reason]}</p></div>
            <div><p className="text-gray-500 text-xs">Importo coperto</p><p className="font-medium">{fmtEuro(claim.amount)}</p><p className="text-xs">Richiesto {fmtEuro(claim.requested_amount)}</p></div>
          </div>
          <p className="mt-4 text-sm bg-gray-50 border border-gray-200 rounded-md p-3 whitespace-pre-line">{claim.description}</p>
          <div className="mt-6 space-y-4 border-t border-gray-200 pt-5">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Stato</Label>
                <Select value={f.status} onValueChange={(status) => setF({ ...f, status })}>
                  <SelectTrigger data-testid="claim-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CLAIM_STATUS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`claim-status-option-${k}`}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Rimborso concesso (€)</Label><Input type="number" min="0" step="0.01" value={f.refund_amount} onChange={(e) => setF({ ...f, refund_amount: e.target.value })} data-testid="claim-refund-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>Messaggio al cliente (inviato via email)</Label><Textarea rows={3} value={f.public_message} onChange={(e) => setF({ ...f, public_message: e.target.value })} data-testid="claim-public-message-input" /></div>
            <div className="space-y-1.5"><Label>Nota interna (non visibile al cliente)</Label><Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="claim-note-input" /></div>
            <Button className="bg-emerald-900 hover:bg-emerald-800" onClick={save} data-testid="claim-save-button">Salva aggiornamento</Button>
          </div>
          <div className="mt-6"><History c={claim} /></div>
        </>}
      </SheetContent>
    </Sheet>
  );
};

export default function Claims() {
  const [status, setStatus] = useState("all");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/claims", { params: status !== "all" ? { status } : {} });
    setItems(data.items);
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="fade-up">
      <PageHeader title="Garanzia Rocard" sub="Pratiche aperte dai clienti, stile A-Z" />
      <Tabs value={status} onValueChange={setStatus} className="mb-4">
        <TabsList>
          <TabsTrigger value="all" data-testid="claims-filter-all">Tutte</TabsTrigger>
          {Object.entries(CLAIM_STATUS).map(([k, v]) => <TabsTrigger key={k} value={k} data-testid={`claims-filter-${k}`}>{v}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((c) => (
          <button key={c.id} onClick={() => setOpen(c)} className="text-left bg-white border border-gray-200 rounded-lg p-5 hover:border-emerald-900/40 transition-colors" data-testid={`claim-card-${c.id}`}>
            <div className="flex justify-between items-center"><span className="font-mono font-bold text-emerald-900">{c.claim_number}</span><ClaimStatusBadge status={c.status} /></div>
            <p className="mt-3 font-semibold">{c.name}</p>
            <p className="text-sm text-gray-600">#{c.order_number} · {CLAIM_REASONS[c.reason]}</p>
            <div className="mt-3 flex justify-between text-sm"><span className="font-semibold">{fmtEuro(c.amount)}</span><span className="text-gray-500 text-xs">{fmtDateTime(c.created_at)}</span></div>
          </button>
        ))}
        {!items.length && <div className="md:col-span-3 bg-white border border-dashed border-gray-300 rounded-lg p-12 text-center text-sm text-gray-600" data-testid="claims-empty">Nessuna pratica.</div>}
      </div>
      <ClaimSheet claim={open} onClose={() => setOpen(null)} onSaved={(c) => { setOpen(c); load(); }} />
    </div>
  );
}
